import React from 'react';
import PropTypes from 'prop-types';
import { geocodeByAddress } from 'react-places-autocomplete';
import i18next from 'i18next';

import { debounce } from 'utils/general';
import { logError } from 'utils/log';
import APIService from 'services/api';
import { currency } from 'globals/currency';
import StreetAutocomplete from './StreetAutocomplete';
import type { NormalizedRestaurant } from 'types';
import type { TDeliveryEstimate } from 'types/delivery';

import './location-search-input.scss';

declare global {
  interface Window {
    google: any;
  }
}

export interface ILocationSearchAddress {
  city?: string;
  street: string;
  homeNumber: string;
  flatNumber?: string | null;
  lat?: number | string;
  lng?: number | string;
}

interface LocationSearchInputProps {
  address: ILocationSearchAddress;
  onAddressUpdate: (address: any) => void;
  /**
   * When provided, the component uses the restaurant deliveries estimate endpoint
   * (distance + delivery cost for restaurant + pricing availability) instead of
   * the customer delivery price endpoint, and reports the result to the parent.
   */
  onEstimateUpdate?: (estimate: TDeliveryEstimate | null) => void;
  restaurant: NormalizedRestaurant;
}

interface LocationSearchInputState {
  deliveryPriceMessage?: string;
  deliveryPriceLoading?: boolean;
  error: string | null;
}

class LocationSearchInput extends React.Component<
  LocationSearchInputProps,
  LocationSearchInputState
> {
  searchOptions: {
    location: any;
    radius: number;
    types?: string[];
  };
  debouncedGeocode: () => void;
  homeNumberRef?: HTMLInputElement;
  geocodeRequestId = 0;

  static propTypes = {
    address: PropTypes.object.isRequired,
    onAddressUpdate: PropTypes.func.isRequired,
    onEstimateUpdate: PropTypes.func,
    restaurant: PropTypes.object.isRequired,
  };

  constructor(props: LocationSearchInputProps) {
    super(props);
    this.state = {
      error: null,
    };
    try {
      const { restaurant } = props;
      this.searchOptions = {
        location: new window.google.maps.LatLng(restaurant.lat, restaurant.lng),
        radius: 20000,
        types: ['address'],
      };
    } catch (e) {
      logError(
        'Restaurant can order delivery, but has no long/lat set!',
        e as Error
      );
      this.searchOptions = {
        location: new window.google.maps.LatLng(51.247119, 22.563311),
        radius: 1000,
      };
    }
    this.debouncedGeocode = debounce(this.geocode, 1000);
  }

  componentDidUpdate(prevProps: LocationSearchInputProps) {
    const prev = prevProps.address || {};
    const current = this.props.address || {};
    const addressChanged =
      prev.city !== current.city ||
      prev.street !== current.street ||
      prev.homeNumber !== current.homeNumber;

    if (!addressChanged) {
      return;
    }

    // address text changed - previous coordinates & estimate are no longer valid
    this.invalidateEstimate();

    if (this.isAddressComplete()) {
      this.debouncedGeocode();
    } else {
      this.setState({ deliveryPriceMessage: undefined, deliveryPriceLoading: false });
    }
  }

  isAddressComplete = (): boolean => {
    const { city, street, homeNumber } = this.props.address || {};
    return !!(city && city.trim() && street && street.trim() && homeNumber && homeNumber.trim());
  };

  invalidateEstimate = () => {
    this.geocodeRequestId += 1;
    this.props.onEstimateUpdate && this.props.onEstimateUpdate(null);
  };

  geocode = () => {
    if (!this.isAddressComplete()) {
      return;
    }
    const requestId = ++this.geocodeRequestId;
    const isCurrent = () => requestId === this.geocodeRequestId;

    this.setState({ deliveryPriceLoading: true, error: null });
    const { city, street, homeNumber } = this.props.address || {};
    const address = `${street} ${homeNumber}, ${city}`;
    geocodeByAddress(address)
      .then((results) => {
        if (!isCurrent()) {
          return;
        }
        const selectedAddress = results[0];
        const { location } = selectedAddress.geometry;
        const lng = location.lng();
        const lat = location.lat();
        this.props.onAddressUpdate({
          ...this.props.address,
          lng,
          lat,
        });
        return this.props.onEstimateUpdate
          ? this.fetchRestaurantEstimate({ lng, lat, address, isCurrent })
          : this.fetchCustomerDeliveryPrice({ lng, lat, address, isCurrent });
      })
      .catch((error) => {
        if (!isCurrent()) {
          return;
        }
        this.setState({
          error: i18next.t('location-search-input.street-error'),
          deliveryPriceLoading: false,
        });
        logError('Geocode error', error);
      });
  };

  fetchCustomerDeliveryPrice = ({
    lng,
    lat,
    address,
    isCurrent,
  }: {
    lng: number;
    lat: number;
    address: string;
    isCurrent: () => boolean;
  }) => {
    const url = `/restaurants/${this.props.restaurant.id}/distance?lng=${lng}&lat=${lat}`;
    return APIService.get(url)
      .then((deliveryData) => {
        if (!isCurrent()) {
          return;
        }
        const { distance, price: deliveryPrice } = deliveryData;
        this.setState({
          deliveryPriceMessage: i18next.t(
            'location-search-input.delivery-price-message',
            { address, distance, deliveryPrice, currency }
          ),
          deliveryPriceLoading: false,
          error: null,
        });
      })
      .catch((e) => {
        if (!isCurrent()) {
          return;
        }
        this.setState({
          error: i18next.t('location-search-input.distance-error'),
          deliveryPriceLoading: false,
        });
        logError(`Distance measuring error`, e);
      });
  };

  fetchRestaurantEstimate = ({
    lng,
    lat,
    address,
    isCurrent,
  }: {
    lng: number;
    lat: number;
    address: string;
    isCurrent: () => boolean;
  }) => {
    const url = `/restaurants/${this.props.restaurant.id}/deliveries/estimate?lng=${lng}&lat=${lat}`;
    return APIService.get(url)
      .then((estimate: TDeliveryEstimate) => {
        if (!isCurrent()) {
          return;
        }
        this.props.onEstimateUpdate && this.props.onEstimateUpdate(estimate);
        if (!estimate.hasPricing) {
          this.setState({
            deliveryPriceMessage: undefined,
            deliveryPriceLoading: false,
            error: i18next.t('location-search-input.no-pricing-error', {
              address,
              distance: estimate.distance,
            }),
          });
          return;
        }
        this.setState({
          deliveryPriceMessage: i18next.t(
            'location-search-input.delivery-cost-message',
            {
              address,
              distance: estimate.distance,
              deliveryCost: estimate.restaurantCost,
              currency,
            }
          ),
          deliveryPriceLoading: false,
          error: null,
        });
      })
      .catch((e) => {
        if (!isCurrent()) {
          return;
        }
        this.props.onEstimateUpdate && this.props.onEstimateUpdate(null);
        this.setState({
          error: i18next.t('location-search-input.distance-error'),
          deliveryPriceLoading: false,
        });
        logError(`Delivery estimate error`, e);
      });
  };

  handleStreetChange = (street) =>
    this.props.onAddressUpdate({
      ...this.props.address,
      street,
    });

  handleSelect = (suggestionStr: string) => {
    // google suggestion format: "Street, City, Country" (locality parts may vary)
    const addressParts = suggestionStr.split(',').map((part) => part.trim());
    const [street, city] = addressParts;
    this.props.onAddressUpdate({
      ...this.props.address,
      street,
      city: city || this.props.address?.city || '',
    });
    this.homeNumberRef?.focus();
  };

  handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const city = e.target.value;
    this.props.onAddressUpdate({
      ...this.props.address,
      city,
    });
  };

  handleHomeNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const homeNumber = e.target.value;
    this.props.onAddressUpdate({
      ...this.props.address,
      homeNumber,
    });
  };

  handleFlatNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const flatNumber = e.target.value;
    this.props.onAddressUpdate({
      ...this.props.address,
      flatNumber,
    });
  };

  setHomeNumberRef = (node: HTMLInputElement) => (this.homeNumberRef = node);

  render() {
    const address = this.props.address || {};
    const { city = '', flatNumber = '', homeNumber = '', street = '' } = address;
    return (
      <div className="row mb-3">
        <StreetAutocomplete
          street={street}
          searchOptions={this.searchOptions}
          handleStreetChange={this.handleStreetChange}
          handleSelect={this.handleSelect}
          wrapperClassName="col-12 p-0"
        />
        <div className="col-12 p-0">
          <input
            type="text"
            value={city || ''}
            onChange={this.handleCityChange}
            className="form-control mt-2"
            placeholder={i18next.t('location-search-input.city')}
            required
          />
        </div>
        <div className="col-12 col-sm-6 p-0">
          <input
            type="string"
            value={homeNumber}
            ref={this.setHomeNumberRef}
            onChange={this.handleHomeNumberChange}
            className="form-control mt-2"
            placeholder={i18next.t('location-search-input.building-number')}
          />
        </div>
        <div className="col-12 col-sm-6 p-0 pl-sm-2">
          <input
            type="number"
            value={flatNumber || ''}
            onChange={this.handleFlatNumberChange}
            className="form-control mt-2"
            placeholder={i18next.t('location-search-input.flat-number')}
          />
        </div>
        {this.renderFooter()}
      </div>
    );
  }

  renderFooter() {
    const { deliveryPriceMessage, deliveryPriceLoading, error } = this.state;
    if (deliveryPriceLoading) {
      return (
        <div className="text-success mt-2">
          {i18next.t('location-search-input.calculating')}
        </div>
      );
    }
    if (error) {
      return <div className="text-danger mt-2">{error}</div>;
    }
    return (
      deliveryPriceMessage && (
        <div className="text-success mt-2">{deliveryPriceMessage}</div>
      )
    );
  }
}

export default LocationSearchInput;
